import React, { useState, useEffect } from 'react';
import apiClient from '../../../utils/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Sparkles, Home, Trophy, ArrowRight, Loader2, Clock, Layers } from 'lucide-react';

const ICONS = {
  sparkles: Sparkles,
  home: Home,
  trophy: Trophy,
};

export const ReelTemplatePicker = ({ propertyData, onTemplateApplied }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [openHouseDate, setOpenHouseDate] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get('/reels/templates');
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error('Failed to load reel templates');
    }
    setLoading(false);
  };

  const handleApply = async (template) => {
    setApplying(template.template_id);
    try {
      const payload = {
        property_data: propertyData || {},
        open_house_date: openHouseDate || 'This Weekend',
      };
      const res = await apiClient.post(`/reels/templates/${template.template_id}/apply`, payload);
      toast.success(`"${template.name}" template applied!`);
      onTemplateApplied(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply template');
    }
    setApplying(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="reel-template-picker">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold">Choose a Reel Template</h2>
        <p className="text-sm text-muted-foreground mt-1">Pre-built styles with smart overlays and AI voiceover scripts</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {templates.map((tpl) => {
          const Icon = ICONS[tpl.icon] || Sparkles;
          const isOpenHouse = tpl.category === 'open_house';
          const tracks = tpl.tracks || tpl.tracks_config || [];

          return (
            <div
              key={tpl.template_id}
              className="relative group border border-border/50 rounded-xl p-5 bg-card hover:border-border transition-all hover:shadow-lg"
              data-testid={`reel-template-${tpl.template_id}`}
            >
              {/* Color accent bar */}
              <div className="absolute top-0 left-4 right-4 h-1 rounded-b-full" style={{ backgroundColor: tpl.color }} />

              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: `${tpl.color}15` }}
              >
                <Icon className="h-6 w-6" style={{ color: tpl.color }} />
              </div>

              {/* Title + Description */}
              <h3 className="font-semibold text-base mb-1">{tpl.name}</h3>
              <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{tpl.description}</p>

              {/* Stats */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Layers className="h-3 w-3" />
                  {tracks.length} slides
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {tpl.total_duration}s
                </span>
              </div>

              {/* Track slots preview */}
              <div className="flex gap-0.5 h-5 rounded overflow-hidden mb-3">
                {tracks.map((slot, idx) => (
                  <div
                    key={idx}
                    className="flex-1 rounded-sm flex items-center justify-center text-[9px] font-medium text-white"
                    style={{ backgroundColor: tpl.color, opacity: 0.5 + (idx * 0.1) }}
                    title={slot.label}
                  >
                    {slot.duration}s
                  </div>
                ))}
              </div>

              {/* Open house date input */}
              {isOpenHouse && (
                <div className="mb-3">
                  <Label className="text-xs text-muted-foreground">Open House Date</Label>
                  <Input
                    value={openHouseDate}
                    onChange={(e) => setOpenHouseDate(e.target.value)}
                    placeholder="e.g. Sat, March 15th 1-4pm"
                    className="h-8 text-xs mt-1"
                    data-testid="open-house-date-input"
                  />
                </div>
              )}

              {/* Apply button */}
              <Button
                className="w-full mt-1 gap-2"
                size="sm"
                style={{ backgroundColor: tpl.color, color: 'white' }}
                onClick={() => handleApply(tpl)}
                disabled={applying === tpl.template_id}
                data-testid={`apply-template-${tpl.template_id}`}
              >
                {applying === tpl.template_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Use Template <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Blank option */}
      <div className="text-center mt-5">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={() => onTemplateApplied(null)}
          data-testid="start-blank-reel"
        >
          or start with a blank reel
        </Button>
      </div>
    </div>
  );
};
